import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Badge — Luminous Atrium chips.
 *
 * Default = pill eyebrow with bg dot convention. Use `variant="solid"` for filled,
 * `variant="indicator"` for status pills with a tinted fill matching their semantic.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Atrium pill eyebrow — the standard chip across the product
        default:
          "rounded-full border border-border/60 bg-background/70 backdrop-blur px-3 py-1 uppercase tracking-[0.18em] text-[10px] font-medium text-foreground/75 shadow-[0_2px_8px_-4px_rgba(21,34,27,0.06)]",
        secondary:
          "rounded-full border border-transparent bg-secondary px-3 py-1 uppercase tracking-[0.18em] text-[10px] font-medium text-foreground/70",
        outline:
          "rounded-full border border-foreground/20 bg-transparent px-3 py-1 text-[12px] font-medium text-foreground",
        // Filled solid forest pill (rare — for emphasis)
        solid:
          "rounded-full border border-primary bg-primary px-3 py-1 uppercase tracking-[0.18em] text-[10px] font-medium text-primary-foreground",
        destructive:
          "rounded-full border border-destructive/30 bg-destructive/10 px-3 py-1 uppercase tracking-[0.18em] text-[10px] font-medium text-destructive",
        success:
          "rounded-full border border-success/30 bg-success/10 px-3 py-1 uppercase tracking-[0.18em] text-[10px] font-medium text-success",
        warning:
          "rounded-full border border-warning/35 bg-warning/15 px-3 py-1 uppercase tracking-[0.18em] text-[10px] font-medium text-warning",
        info:
          "rounded-full border border-info/30 bg-info/10 px-3 py-1 uppercase tracking-[0.18em] text-[10px] font-medium text-info",
        // Status indicator (sentence-case, soft fill)
        indicator:
          "rounded-full border border-border/60 bg-secondary px-3 py-1 text-[12px] font-medium text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return <div ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />;
  }
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
